import { NotificationService } from '@shared/feature/notification-handling';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CropperPosition, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper/public-api';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { Observable, Subject } from 'rxjs';
import { UploadImageService } from '../../services/upload-image.service';
import { getImageFormats } from '@shared/utils/file-formats';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Component({
  selector: 'feature-upload-file-upload-picture-modal',
  templateUrl: './upload-picture-modal.component.html',
  styleUrls: ['./upload-picture-modal.component.css'],
  standalone: false,
})
export class UploadPictureModalComponent implements OnInit, OnDestroy {
  @ViewChild('cropper') imageCropper?: ImageCropperComponent;
  @ViewChild('input') input?: ElementRef;

  cropperPosition: CropperPosition = { x1: 0, y1: 0, x2: 125, y2: 125 };
  transform: ImageTransform = { scale: 1 };
  imageChangedEvent: Event | null = null;
  selectedImage: File | null = null;
  accept = getImageFormats();
  cropperSize = 125;
  imageSize = 250;

  uploadImageService!: UploadImageService;

  public loading$!: Observable<boolean>;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(private notificationService: NotificationService, private translationService: TranslationFromJsonService) {}

  ngOnInit(): void {
    this.loading$ = this.uploadImageService.isLoading();
  }

  browseFiles(): void {
    this.input?.nativeElement.click();
  }

  fileChangeEvent(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;
    if (files && files.length > 0) {
      this.resizeSmallImages(files[0])
        .then((res) => {
          if (res && this.input) {
            this.input.nativeElement.files = this.createFileList(res);
          }
          this.imageChangedEvent = event;
          this.resetChanges();
        })
        .catch((err: Error) => {
          this.selectedImage = null;
          this.notificationService.addError(err.message);
        });
    }
  }

  imageCropped(event: ImageCroppedEvent): void {
    if (event.blob) {
      const reader = new FileReader();
      reader.readAsDataURL(event.blob);
      reader.onload = () => {
        const base64String = reader.result as string;
        this.createCircularImage(base64String);
      };
    }
  }

  loadImageFailed(): void {
    this.notificationService.addError(this.translationService.instant('upload-file-media-type-not-supported'));
    this.selectedImage = null;
  }

  cropperReady(): void {
    this.centerCropper();
  }

  zoomChanged(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.transform = { ...this.transform, scale: +value };
  }

  uploadImage(): void {
    this.imageCropper?.crop();
  }

  mouseZoom(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY > 0) {
      this.transform = { ...this.transform, scale: Math.max((this.transform.scale || 1) - 0.1, 0.5) };
    } else {
      this.transform = { ...this.transform, scale: Math.min((this.transform.scale || 1) + 0.1, 2.5) };
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && this.input) {
      this.input.nativeElement.files = files;
      this.input.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.removeDragOverStyles();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragOverStyles();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.removeDragOverStyles();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private createCircularImage(base64String: string): void {
    const img = new Image();
    img.src = base64String;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = Math.min(img.width, img.height);

      canvas.width = size;
      canvas.height = size;

      if (ctx) {
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, 0, 0, size, size);
      }

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'image.png', { type: 'image/png' });
          this.uploadImageService.uploadImage(file);
        }
      }, 'image/png');
    };
  }

  private centerCropper(): void {
    const center = this.imageSize / 2 - this.cropperSize / 2;
    this.cropperPosition = { x1: center, y1: center, x2: center + this.cropperSize, y2: center + this.cropperSize };
  }

  private resetChanges(): void {
    this.transform = { scale: 1 };
    this.centerCropper();
  }

  private setDragOverStyles(): void {
    const dropFilesDiv = document.querySelector('.drop-files');
    dropFilesDiv?.classList.add('drag-over');
  }

  private removeDragOverStyles(): void {
    const dropFilesDiv = document.querySelector('.drop-files');
    dropFilesDiv?.classList.remove('drag-over');
  }

  private async resizeSmallImages(file: File): Promise<File | null> {
    return new Promise((resolve) => {
      let resizedFile: File | null = null;

      if (!file) {
        resolve(resizedFile);
      }

      if (!this.isFileTypeImage(file)) {
        throw new Error(this.translationService.instant('upload-file-media-type-not-supported'));
      }

      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const minDimension = this.imageSize;
          let width = img.width;
          let height = img.height;

          const aspectRatio = width / height;

          if (width >= minDimension && height >= minDimension) {
            this.selectedImage = file;
            resolve(resizedFile);

            return;
          }

          if (width <= height) {
            height = Math.round(minDimension * (1 - aspectRatio + 1));
            width = minDimension;
          } else {
            height = minDimension;
            width = Math.round(minDimension * aspectRatio);
          }

          canvas.width = width;
          canvas.height = height;

          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob && this.input) {
                this.selectedImage = new File([blob], file.name, { type: file.type });
                resizedFile = this.selectedImage;
                resolve(resizedFile);
              }
            }, file.type);
          }
        };

        if (e.target) {
          img.src = e.target.result as string;
        }
      };

      reader.readAsDataURL(file);
    });
  }

  private createFileList(file: File): FileList {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    return dataTransfer.files;
  }

  private isFileTypeImage(file: File): boolean {
    const imageTypes = getImageFormats().split(',');

    return imageTypes.includes(file.type);
  }
}
